import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ToastContext } from "../hooks/useToast";
import { ToastViewport } from "../components/ui";
import { registerToastError } from "../utils/toastBridge";
import type { Toast, ToastOptions, ToastType } from "../types/toast";

const DEFAULT_DURATION: Record<ToastType, number> = {
  success: 4000,
  info: 5000,
  error: 8000,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((type: ToastType, message: string, options?: ToastOptions) => {
    counter.current += 1;
    const id = `toast-${counter.current}`;
    const duration = options?.duration ?? DEFAULT_DURATION[type];
    setToasts((prev) => [...prev, { id, type, message, duration }]);
    return id;
  }, []);

  const success = useCallback((message: string, options?: ToastOptions) => show("success", message, options), [show]);
  const error = useCallback((message: string, options?: ToastOptions) => show("error", message, options), [show]);
  const info = useCallback((message: string, options?: ToastOptions) => show("info", message, options), [show]);

  useEffect(() => {
    registerToastError((message) => error(message));
    return () => registerToastError(null);
  }, [error]);

  return (
    <ToastContext.Provider value={{ toasts, show, success, error, info, dismiss }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}
